// =====================================================
// EVENT PARTICIPANTS HOOK
// Stage 2: Participants & Pricing
// React Query hook for managing event participants
// =====================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { eventParticipantsRepository } from '@/data/eventParticipantsRepository';
import type { ParticipantFilters } from '@/Services/eventParticipants.service';
import {
  checkInParticipant,
  markParticipantAbsent,
  resetAttendanceStatus,
} from '@/Services/eventAttendance.service';
import { recordPayment, reversePayment } from '@/Services/eventPayments.service';
import { useAuth } from '@/context/authContext';
import { supabase } from '@/supabase.config';

// ===================
// QUERY KEYS
// ===================

export const eventParticipantsKeys = {
  all: ['eventParticipants'] as const,
  byEvent: (eventId: string) => [...eventParticipantsKeys.all, eventId] as const,
  detail: (id: string) => [...eventParticipantsKeys.all, 'detail', id] as const,
  statistics: (eventId: string) => [...eventParticipantsKeys.all, 'statistics', eventId] as const,
};

// ===================
// QUERIES
// ===================

/**
 * Hook to fetch participants for an event
 */
export function useEventParticipants(eventId: string, filters?: ParticipantFilters) {
  return useQuery({
    queryKey: [...eventParticipantsKeys.byEvent(eventId), filters],
    queryFn: () => eventParticipantsRepository.getEventParticipants(eventId, filters),
    staleTime: 30 * 1000, // short: several admins verify/pay concurrently
    refetchOnWindowFocus: true,
  });
}

/**
 * Hook to fetch a single participant
 */
export function useEventParticipant(id: string, enabled = true) {
  return useQuery({
    queryKey: eventParticipantsKeys.detail(id),
    queryFn: () => eventParticipantsRepository.getParticipantById(id),
    enabled,
  });
}

/**
 * Hook to fetch event participant statistics
 */
export interface EventParticipantStats {
  total: number;
  ministers: number;
  guests: number;
  mandatory: number;
  pre_registered: number;
  verified: number;
  rejected: number;
  paid: number;
  partial: number;
  pending: number;
  collected: number;
  outstanding: number;
  attended: number;
  absent: number;
  mandatory_absent: number;
}

/** Event-wide totals in one query (get_event_participant_stats), independent of table filters */
export function useEventParticipantStatistics(eventId: string) {
  return useQuery({
    queryKey: eventParticipantsKeys.statistics(eventId),
    queryFn: async () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any).rpc('get_event_participant_stats', { p_event_id: eventId });
      if (error) throw new Error(error.message);
      return data as EventParticipantStats;
    },
    staleTime: 60 * 1000,
  });
}

// ===================
// MUTATIONS
// ===================

/**
 * Hook for participant registration mutations
 */
export function useParticipantMutations(eventId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const registerParticipant = useMutation({
    mutationFn: eventParticipantsRepository.registerParticipant,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: eventParticipantsKeys.byEvent(eventId) });
      queryClient.invalidateQueries({ queryKey: eventParticipantsKeys.statistics(eventId) });
      toast({ title: 'Participante registrado exitosamente' });
    },
    onError: (error: Error) => {
      toast({ title: error.message || 'Error al registrar participante', variant: 'destructive' });
    },
  });

  const unregisterParticipant = useMutation({
    mutationFn: eventParticipantsRepository.unregisterParticipant,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: eventParticipantsKeys.byEvent(eventId) });
      queryClient.invalidateQueries({ queryKey: eventParticipantsKeys.statistics(eventId) });
      toast({ title: 'Participante eliminado exitosamente' });
    },
    onError: (error: Error) => {
      toast({ title: error.message || 'Error al eliminar participante', variant: 'destructive' });
    },
  });

  const updateParticipant = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { is_mandatory?: boolean } }) =>
      eventParticipantsRepository.updateParticipant(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: eventParticipantsKeys.byEvent(eventId) });
      queryClient.invalidateQueries({ queryKey: eventParticipantsKeys.statistics(eventId) });
      toast({ title: 'Participante actualizado exitosamente' });
    },
    onError: (error: Error) => {
      toast({ title: error.message || 'Error al actualizar participante', variant: 'destructive' });
    },
  });

  const recalculatePrice = useMutation({
    mutationFn: eventParticipantsRepository.recalculateParticipantPrice,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: eventParticipantsKeys.byEvent(eventId) });
      queryClient.invalidateQueries({ queryKey: eventParticipantsKeys.statistics(eventId) });
      toast({ title: 'Precio recalculado exitosamente' });
    },
    onError: (error: Error) => {
      toast({ title: error.message || 'Error al recalcular precio', variant: 'destructive' });
    },
  });

  const recalculateAllPrices = useMutation({
    mutationFn: () => eventParticipantsRepository.recalculateAllParticipantPrices(eventId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: eventParticipantsKeys.byEvent(eventId) });
      queryClient.invalidateQueries({ queryKey: eventParticipantsKeys.statistics(eventId) });
      toast({ title: 'Todos los precios han sido recalculados' });
    },
    onError: (error: Error) => {
      toast({ title: error.message || 'Error al recalcular precios', variant: 'destructive' });
    },
  });

  const updatePaymentStatus = useMutation({
    mutationFn: ({ id, status, amount }: { id: string; status: 'PAID' | 'PENDING'; amount?: number }) =>
      eventParticipantsRepository.updateParticipantPaymentStatus(id, status, amount),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: eventParticipantsKeys.byEvent(eventId) });
      queryClient.invalidateQueries({ queryKey: eventParticipantsKeys.statistics(eventId) });
      toast({ title: 'Estado de pago actualizado' });
    },
    onError: (error: Error) => {
      toast({ title: error.message || 'Error al actualizar pago', variant: 'destructive' });
    },
  });

  const bulkUpdatePaymentStatus = useMutation({
    mutationFn: ({ ids, status }: { ids: string[]; status: 'PAID' | 'PENDING' }) =>
      eventParticipantsRepository.bulkUpdatePaymentStatus(ids, status),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: eventParticipantsKeys.byEvent(eventId) });
      queryClient.invalidateQueries({ queryKey: eventParticipantsKeys.statistics(eventId) });
      toast({ 
        title: 'Pagos actualizados', 
        description: `${result.updated} actualizados${result.errors > 0 ? `, ${result.errors} errores` : ''}` 
      });
    },
    onError: (error: Error) => {
      toast({ title: error.message || 'Error al actualizar pagos', variant: 'destructive' });
    },
  });

  return {
    registerParticipant: registerParticipant.mutate,
    unregisterParticipant: unregisterParticipant.mutate,
    updateParticipant: updateParticipant.mutate,
    recalculatePrice: recalculatePrice.mutate,
    recalculateAllPrices: recalculateAllPrices.mutate,
    updatePaymentStatus: updatePaymentStatus.mutate,
    bulkUpdatePaymentStatus: bulkUpdatePaymentStatus.mutate,
    isRegistering: registerParticipant.isPending,
    isUnregistering: unregisterParticipant.isPending,
    isUpdating: updateParticipant.isPending,
    isRecalculating: recalculatePrice.isPending || recalculateAllPrices.isPending,
    isUpdatingPayment: updatePaymentStatus.isPending || bulkUpdatePaymentStatus.isPending,
  };
}

// ===================
// ATTENDANCE MUTATIONS
// ===================

export function useAttendanceMutations(eventId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user } = useAuth();

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: eventParticipantsKeys.byEvent(eventId) });
    queryClient.invalidateQueries({ queryKey: eventParticipantsKeys.statistics(eventId) });
    queryClient.invalidateQueries({ queryKey: ['event-attendance-stats', eventId] });
  };

  const checkIn = useMutation({
    mutationFn: (participantId: string) =>
      checkInParticipant({ participant_id: participantId, checked_in_by: user?.id }),
    onSuccess: () => {
      invalidate();
    },
    onError: (error: Error) => {
      toast({ title: 'Error al registrar asistencia', description: error.message, variant: 'destructive' });
    },
  });

  const markAbsent = useMutation({
    mutationFn: markParticipantAbsent,
    onSuccess: () => {
      invalidate();
    },
    onError: (error: Error) => {
      toast({ title: 'Error al marcar ausencia', description: error.message, variant: 'destructive' });
    },
  });

  const resetStatus = useMutation({
    mutationFn: resetAttendanceStatus,
    onSuccess: () => {
      invalidate();
    },
    onError: (error: Error) => {
      toast({ title: 'Error al reiniciar estado', description: error.message, variant: 'destructive' });
    },
  });

  return {
    checkIn: checkIn.mutateAsync,
    markAbsent: markAbsent.mutateAsync,
    resetStatus: resetStatus.mutateAsync,
    isUpdating: checkIn.isPending || markAbsent.isPending || resetStatus.isPending,
  };
}

// ===================
// PAYMENT MUTATIONS
// ===================

export function usePaymentMutations(eventId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: eventParticipantsKeys.byEvent(eventId) });
    queryClient.invalidateQueries({ queryKey: eventParticipantsKeys.statistics(eventId) });
    queryClient.invalidateQueries({ queryKey: ['event-attendance-stats', eventId] });
  };

  const record = useMutation({
    mutationFn: recordPayment,
    onSuccess: () => {
      invalidate();
    },
    onError: (error: Error) => {
      toast({ title: 'Error al registrar pago', description: error.message, variant: 'destructive' });
    },
  });

  const reverse = useMutation({
    mutationFn: reversePayment,
    onSuccess: () => {
      invalidate();
    },
    onError: (error: Error) => {
      toast({ title: 'Error al revertir pago', description: error.message, variant: 'destructive' });
    },
  });

  return {
    recordPayment: record.mutateAsync,
    reversePayment: reverse.mutateAsync,
    isProcessing: record.isPending || reverse.isPending,
  };
}

/**
 * Hook to check if minister/guest is already registered
 */
export function useCheckRegistration(
  eventId: string,
  ministerId?: string,
  guestId?: string,
  enabled = true
) {
  return useQuery({
    queryKey: [...eventParticipantsKeys.byEvent(eventId), 'check', ministerId, guestId],
    queryFn: () => eventParticipantsRepository.isAlreadyRegistered(eventId, ministerId, guestId),
    enabled: enabled && (!!ministerId || !!guestId),
    staleTime: 30 * 1000, // 30 seconds
  });
}
